import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button, Modal, Spin, message } from "antd";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import {
  acknowledgeFinishedWork,
  getHomeFlow,
} from "../../../service/homeFlow";
import { fetchMatter, getShareRecipientsApi } from "../../../service/matter";
import { updateInboxTaskStatus } from "../../../service/inbox";
import { normalizeShareRecipients } from "../../../utils/shareRecipients";
import { WorkItemCard } from "../../../components/WorkItems/MatterWorkPanel";
import ReviewCard, { reviewStanceOf } from "./ReviewCard";
import CreateWorkItemModal from "../../../components/Inbox/CreateWorkItemModal";
import { splitMatterLabel } from "../../../utils/matterLabel";
import {
  CATEGORY_META,
  activationOf,
  ageTone,
  dueInfo,
  groupByCategory,
  groupByClient,
  onAClockRows,
  partitionByActivation,
  rowTagOf,
  stateLineFor,
} from "./plate";
import WorkFold from "./WorkFold";
import FinishedStrip from "./FinishedStrip";
import BoardAppealsTile from "./BoardAppealsTile";
import PackedCards from "./PackedCards";
import {
  AgeText,
  ClockDueCell,
  ClockExpandCell,
  ClockTable,
  ClockTableWrap,
  ClockTitleButton,
  DegradedBanner,
  DueChip,
  KindDot,
  KindTag,
  LoadingNote,
  MatterCard,
  MatterCardHeader,
  MatterOption,
  PickerHint,
  PickerStatus,
  PlateEmpty,
  QuietNote,
  RowBody,
  RowExpand,
  RowFlag,
  RowMeta,
  RowShell,
  RowTitleButton,
  RowTop,
  RowVerbButton,
  SearchInput,
  SearchWrap,
  SectionHeaderRow,
  SectionSpacer,
  SectionTitle,
  StateLine,
  SuggestionPill,
  SuggestionsRow,
  TogglePill,
  TopBar,
  ViewingBanner,
  ViewingBannerSpacer,
} from "./MyWorkStyled";

/**
 * My Work — the Home landing surface, rebuilt to the approved 8/31
 * mock (Matthew's redlines on the previous ship: "it's still just one
 * big fucking dumb list… headed by client and all the work to be done
 * for that client"; no "start working" banner; deadlines unmissable;
 * "I can search a name and pull up the same view as mine").
 *
 * Top to bottom (8/31 evening redlines applied — "lose the plate
 * analogy", "Due Soon", "make windows", "By category"):
 *
 *   1. **Person search** (top right) — type a name, pick a teammate,
 *      see their work in this exact layout, read-only (all action
 *      verbs hidden; rows still open documents/matters).
 *   2. **Due Soon** — every row with a real deadline in the next two
 *      weeks (or overdue), soonest first, in a table. This is why
 *      CAVC court dates can never hide inside a card. A work item here
 *      opens the same card, with the same verbs, as the copy of it
 *      inside its window (9/8) — the table is a second view of the
 *      same work, never a lesser one.
 *   3. **The windows** — cards packed into as many 430px columns as
 *      fit, each card dealt to the shortest column so a one-item card
 *      never floats under a tall neighbour's white space (PackedCards;
 *      Matthew, 9/10: "Harold Burke needs to move up a bit"), grouped
 *      BY CLIENT by default (one card
 *      per matter, rows ordered by sub-type then urgency: "organized
 *      first by client and then by the sub-type"), with a one-click
 *      flip to BY CATEGORY — which is a CAVC window, a BVA Briefs
 *      window, and then a card per client for everything else
 *      (Matthew, 9/9: "I personally like my 'CAVC' and 'BVA Brief'
 *      categories but most everything else can be organized by
 *      client"). The choice persists.
 *
 * Rows are compact — the due/age meta sits with the rest of the meta
 * under the title, not across a desert of white space. Work items
 * expand in place to the matter panel's WorkItemCard (Return / Send
 * back / Accept & close — the doorway's verbs, wiring untouched);
 * documents sent for review expand to ReviewCard, which carries the
 * verbs that exchange has always lacked on this page (Approve / send
 * back, or Filed / Done with the date it happened — Matthew, 9/10:
 * "I need everyone to have the same version of work docs I do");
 * signatures open their document (where the signing experience
 * lives); tasks complete inline through the inbox task endpoint.
 * **Create Work Item** (the new-org baton, not the legacy Litify
 * task) starts with a matter picker, then the same composer the
 * matter dashboard uses.
 *
 * No counts anywhere. `waiting_on_others` still arrives in the
 * payload but is not rendered here — the person search replaced the
 * team lane. Backend contract: dms_backend #2063 (`category` on every
 * row; `?user=` for a colleague's view; `viewing` names whose work
 * this is). Older backends omit `category` or send retired values —
 * plate.js translates (court→cavc) and falls back by kind.
 */

const GROUP_MODE_KEY = "myWorkGroupMode";

// Keep the page current without a Refresh button: poll while it's in
// view, catch up the instant the tab is looked at again, and give
// Salesforce a second look after a create (the first reload can beat
// the write).
const POLL_MS = 15000;
const CREATE_CATCHUP_MS = 1500;

const readStoredGroupMode = () => {
  try {
    return localStorage.getItem(GROUP_MODE_KEY) === "kind"
      ? "kind"
      : "client";
  } catch {
    return "client";
  }
};

/** Every piece of work opens in place wherever it appears — inside a
 * window or in Due Soon (Matthew, 9/8: "there shouldn't be any
 * difference in the work item entry between the 'due soon' and regular
 * categories"). That now covers documents sent for review as well as
 * work items (Matthew, 9/10: "I need everyone to have the same version
 * of work docs I do") — see ReviewCard for why the two ever differed.
 * Someone else's plate stays read-only, so nothing expands there. */
const canExpandInPlace = (row, readOnly) => {
  if (readOnly) return false;
  if (row.kind === "work_item") return Boolean(row.work_item);
  return reviewStanceOf(row, readOnly) !== null;
};

/** The card behind an expanded row: the work-item card for a work
 * item, the review card for a document sent for review. One row, one
 * place it opens, whichever kind of work it turns out to be. */
const ExpandedWork = ({ row, matterLabel, readOnly, onActed, onOpen }) => {
  if (row.kind === "work_item") {
    return (
      <WorkItemCard
        item={row.work_item}
        matterLabel={matterLabel}
        matterId={row.matter_id ?? undefined}
        defaultExpanded
        onActed={onActed}
        closeLabel="Accept & close"
      />
    );
  }
  const stance = reviewStanceOf(row, readOnly);
  if (!stance) return null;
  return (
    <ReviewCard
      row={row}
      stance={stance}
      matterLabel={matterLabel}
      onActed={onActed}
      onOpenDocument={onOpen}
    />
  );
};

ExpandedWork.propTypes = {
  row: PropTypes.object.isRequired,
  matterLabel: PropTypes.string,
  readOnly: PropTypes.bool,
  onActed: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
};

const userLabel = (user) =>
  user?.full_name || user?.username || user?.email || `User #${user?.id}`;

/** Type a name, get clickable teammate suggestions. The list reuses
 * the share-dialog people source and loads once, on first use. */
const PersonSearch = ({ onPick }) => {
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState(null);
  const fetchState = useRef("idle");

  const ensureUsers = useCallback(() => {
    if (fetchState.current !== "idle") return;
    fetchState.current = "loading";
    getShareRecipientsApi(null, false)
      .then((response) => {
        fetchState.current = "done";
        setUsers(normalizeShareRecipients(response));
      })
      .catch(() => {
        fetchState.current = "idle";
        setUsers([]);
      });
  }, []);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || !users) return [];
    return users
      .filter(
        (user) =>
          user?.id != null &&
          (userLabel(user).toLowerCase().includes(q) ||
            (user.email || "").toLowerCase().includes(q))
      )
      .slice(0, 6);
  }, [query, users]);

  return (
    <SearchWrap>
      <SearchInput
        type="search"
        value={query}
        placeholder="See someone's work — type a name…"
        aria-label="See someone's work"
        onFocus={ensureUsers}
        onChange={(e) => {
          ensureUsers();
          setQuery(e.target.value);
        }}
      />
      {matches.length > 0 && (
        <SuggestionsRow>
          {matches.map((user) => (
            <SuggestionPill
              key={user.id}
              type="button"
              onClick={() => {
                setQuery("");
                onPick({ key: String(user.id), name: userLabel(user) });
              }}
            >
              {userLabel(user)}
            </SuggestionPill>
          ))}
        </SuggestionsRow>
      )}
    </SearchWrap>
  );
};

PersonSearch.propTypes = { onPick: PropTypes.func.isRequired };

/** The row's tag: the item's own type for a plain work item ("Order
 * exam", "Get statement"), the category otherwise (Matthew, 9/9 — a
 * generic "Handoff" tag said nothing). */
const CategoryTag = ({ row }) => {
  const meta = rowTagOf(row);
  return (
    <KindTag>
      <KindDot $color={meta.dot} />
      {meta.label}
    </KindTag>
  );
};

const formatMD = (iso) => {
  const parsed = dayjs(iso);
  return parsed.isValid() ? parsed.format("M/D") : "";
};

/** The at-a-glance progress flags on a collapsed work-item row: the
 * follow-up date (red once past), "no document" on a returned item
 * that should have come with paper, and the most recent logged step.
 * Expanding the row shows the whole chip strip in WorkItemCard. */
const RowProgress = ({ row }) => {
  const item = row.work_item;
  if (!item) return null;
  const steps = Array.isArray(item.steps) ? item.steps : [];
  const last = steps.length > 0 ? steps[steps.length - 1] : null;
  const followUp = item.followUpOn && item.status !== "Closed";
  if (!last && !followUp && !item.missingDocument) return null;
  return (
    <>
      {last && (
        <RowFlag data-testid={`row-last-step-${row.key}`}>
          {last.label || last.step}
        </RowFlag>
      )}
      {followUp && (
        <RowFlag
          $tone={item.followUpOverdue ? "over" : "flag"}
          data-testid={`row-follow-up-${row.key}`}
        >
          {item.followUpOverdue ? "Follow-up due" : "Follow up"}{" "}
          {formatMD(item.followUpOn)}
        </RowFlag>
      )}
      {item.missingDocument && (
        <RowFlag $tone="over" data-testid={`row-no-document-${row.key}`}>
          No document attached
        </RowFlag>
      )}
    </>
  );
};

RowProgress.propTypes = { row: PropTypes.object.isRequired };

CategoryTag.propTypes = { row: PropTypes.object.isRequired };

/**
 * The first step of Create Work Item from Home: which matter is this
 * on? Reuses the matter-list search, then hands the picked matter to
 * the same composer the matter dashboard uses.
 */
const MatterPicker = ({ open, onClose, onPick }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [failed, setFailed] = useState(false);
  const searchSeq = useRef(0);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults(null);
      setSearching(false);
      setFailed(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const q = query.trim();
    if (q.length < 2) {
      setResults(null);
      setSearching(false);
      setFailed(false);
      return undefined;
    }
    const seq = ++searchSeq.current;
    setSearching(true);
    setFailed(false);
    const timer = setTimeout(() => {
      // Same search, same arguments as the Matters page — real sort
      // values included (empty sort params 500 the matter list, which
      // this picker used to paper over as "No matters match"), AND the
      // same "My Matters" scope the Matters page defaults to. Without
      // assigned_to_me, a query like "shawndu" fuzzy-matches every
      // "Shaw" surname firm-wide (word_similarity scores "shaw" high
      // against "shawndu") and sorts alphabetically, burying the one
      // matter you actually meant under unrelated Shaws (Matthew,
      // 9/1: "same search very different results").
      fetchMatter(1, "", "", "asc", "display_name", 8, q, true)
        .then((response) => {
          if (seq !== searchSeq.current) return;
          setResults(
            Array.isArray(response?.data?.results)
              ? response.data.results
              : []
          );
        })
        .catch(() => {
          if (seq !== searchSeq.current) return;
          setResults(null);
          setFailed(true);
        })
        .finally(() => {
          if (seq === searchSeq.current) setSearching(false);
        });
    }, 250);
    return () => clearTimeout(timer);
  }, [open, query]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title="Create Work Item"
      destroyOnClose
    >
      <PickerHint>Which matter is this on?</PickerHint>
      <SearchInput
        type="search"
        value={query}
        placeholder="Find the matter — client name or number…"
        aria-label="Find the matter"
        autoFocus
        onChange={(e) => setQuery(e.target.value)}
      />
      {searching && <PickerStatus role="status">Searching…</PickerStatus>}
      {!searching && failed && (
        <PickerStatus role="alert">
          Search isn&rsquo;t responding — try again.
        </PickerStatus>
      )}
      {!searching && !failed && results && results.length === 0 && (
        <PickerStatus role="status">No matters match.</PickerStatus>
      )}
      {!searching &&
        (results || []).map((matter) => {
          const label = splitMatterLabel(matter.display_name);
          return (
            <MatterOption
              key={matter.id}
              type="button"
              title={label.full || undefined}
              onClick={() => onPick(matter)}
            >
              {label.short || `Matter ${matter.id}`}
            </MatterOption>
          );
        })}
    </Modal>
  );
};

MatterPicker.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onPick: PropTypes.func.isRequired,
};

/**
 * One row of work: kind tag · title · from · due chip · age. What
 * varies is the verb — work items expand to WorkItemCard, tasks get
 * Complete, reviews/signatures open their document. Read-only mode
 * (someone else's view) hides every verb; titles still open things.
 */
const WorkRow = ({
  row,
  showKind,
  showMatter,
  readOnly,
  expanded,
  onToggleExpand,
  onOpen,
  onCompleteTask,
  taskBusy,
  onActed,
}) => {
  const due = dueInfo(row.due_on);
  const tone = ageTone(row.age_days || 0);
  const matter = splitMatterLabel(row.matter_name);
  const expandable = canExpandInPlace(row, readOnly);
  // Blocked rows render greyed in place, with the "Coming up…" line
  // standing in for the usual due/age meta (Matthew, 9/4) — no verbs
  // except comment/expand, which WorkItemCard itself enforces once
  // opened.
  const blocked = activationOf(row) === "blocked";
  const stateLine = stateLineFor(row);

  return (
    <RowShell data-testid={`work-row-${row.key}`} $muted={blocked}>
      <RowTop>
        {showKind && <CategoryTag row={row} />}
        <RowBody>
          <RowTitleButton
            type="button"
            title={row.title || ""}
            aria-expanded={expandable ? expanded : undefined}
            onClick={expandable ? onToggleExpand : onOpen}
          >
            {row.title || "Untitled"}
          </RowTitleButton>
          {/* Due/age live with the rest of the meta, tight under the
              title — not across a desert of white space (redline #5). */}
          <RowMeta>
            {showMatter && matter.short && (
              <span title={matter.full}>{matter.short}</span>
            )}
            {row.kind === "doc_review" && row.step_line ? (
              <span>{row.step_line}</span>
            ) : (
              row.counterparty && <span>from {row.counterparty}</span>
            )}
            {stateLine ? (
              <StateLine>{stateLine}</StateLine>
            ) : (
              <>
                {due && (
                  <DueChip $urgency={due.urgency} data-urgency={due.urgency}>
                    due {due.label} · {due.detail}
                  </DueChip>
                )}
                {(row.age_days || 0) > 0 && (
                  <AgeText $tone={tone}>{row.age_days}d old</AgeText>
                )}
              </>
            )}
            {!expanded && <RowProgress row={row} />}
          </RowMeta>
        </RowBody>
        {!readOnly && row.kind === "task" && (
          <RowVerbButton
            type="button"
            disabled={taskBusy}
            onClick={onCompleteTask}
          >
            {taskBusy ? "Completing…" : "Complete"}
          </RowVerbButton>
        )}
      </RowTop>
      {expandable && expanded && (
        <RowExpand>
          <ExpandedWork
            row={row}
            matterLabel={matter.short || undefined}
            readOnly={readOnly}
            onActed={onActed}
            onOpen={onOpen}
          />
        </RowExpand>
      )}
    </RowShell>
  );
};

WorkRow.propTypes = {
  row: PropTypes.object.isRequired,
  showKind: PropTypes.bool,
  showMatter: PropTypes.bool,
  readOnly: PropTypes.bool,
  expanded: PropTypes.bool,
  onToggleExpand: PropTypes.func,
  onOpen: PropTypes.func.isRequired,
  onCompleteTask: PropTypes.func,
  taskBusy: PropTypes.bool,
  onActed: PropTypes.func,
};

const MyWorkTab = ({ getRowProps, getReviewRowProps }) => {
  const navigate = useNavigate();
  const [loadStatus, setLoadStatus] = useState("loading");
  const [flow, setFlow] = useState(null);
  // Create Work Item is a two-step: pick the matter, then compose.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [composeMatterId, setComposeMatterId] = useState(null);
  // Whose work we're reading: null = mine; else {key: id, name}.
  const [person, setPerson] = useState(null);
  const [personGone, setPersonGone] = useState(false);
  const [groupMode, setGroupMode] = useState(readStoredGroupMode);
  // Work-item rows expanded in place (own plate only).
  const [expandedKeys, setExpandedKeys] = useState({});
  const [busyTaskKey, setBusyTaskKey] = useState(null);
  // Monotonic request id — a slow earlier response must never
  // overwrite a fresher one (same guard as the matter work panel).
  const requestSeq = useRef(0);
  const personRef = useRef(person);
  personRef.current = person;
  const createCatchupRef = useRef(null);

  const load = useCallback((target, { silent = false } = {}) => {
    const seq = ++requestSeq.current;
    if (!silent) setLoadStatus("loading");
    getHomeFlow(target?.key)
      .then((response) => {
        if (seq !== requestSeq.current) return;
        setFlow(response?.data || null);
        setLoadStatus("ready");
      })
      .catch((err) => {
        if (seq !== requestSeq.current) return;
        if (target && err?.response?.status === 404) {
          // Unknown user: say so quietly and fall back to my plate.
          setPersonGone(true);
          setPerson(null);
          return;
        }
        // A background miss must not blank a page that's already showing.
        if (!silent) setLoadStatus("failed");
      });
  }, []);

  useEffect(() => {
    setExpandedKeys({});
    load(person);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person]);

  const reload = useCallback(
    (options) => load(personRef.current, options),
    [load]
  );

  const reloadAfterCreate = useCallback(() => {
    reload({ silent: true });
    if (createCatchupRef.current) clearTimeout(createCatchupRef.current);
    createCatchupRef.current = setTimeout(() => {
      createCatchupRef.current = null;
      reload({ silent: true });
    }, CREATE_CATCHUP_MS);
  }, [reload]);

  useEffect(() => {
    const refreshIfVisible = () => {
      if (document.visibilityState === "hidden") return;
      reload({ silent: true });
    };
    const timer = setInterval(refreshIfVisible, POLL_MS);
    document.addEventListener("visibilitychange", refreshIfVisible);
    window.addEventListener("focus", refreshIfVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", refreshIfVisible);
      window.removeEventListener("focus", refreshIfVisible);
      if (createCatchupRef.current) clearTimeout(createCatchupRef.current);
    };
  }, [reload]);

  const pickGroupMode = (mode) => {
    setGroupMode(mode);
    try {
      localStorage.setItem(GROUP_MODE_KEY, mode);
    } catch {
      // Private-mode storage failures shouldn't break the toggle.
    }
  };

  const openRow = useCallback(
    (row) => {
      if (row.kind === "work_item") {
        if (row.matter_id) navigate(`/matter/${row.matter_id}`);
        return;
      }
      if (row.document?.relative_path) {
        // Reuse Home's document openers (deleted-file check + viewer
        // deep link). Review rows get the review-notes auto-open;
        // signature/task rows must not (Bugbot, PR #638).
        const factory =
          row.kind === "doc_review" && getReviewRowProps
            ? getReviewRowProps
            : getRowProps;
        const rowProps = factory({ document: row.document });
        rowProps.onClick({ stopPropagation: () => {} });
        return;
      }
      if (row.matter_id) navigate(`/matter/${row.matter_id}`);
    },
    [getRowProps, getReviewRowProps, navigate]
  );

  const completeTask = useCallback(
    (row) => {
      setBusyTaskKey(row.key);
      updateInboxTaskStatus(row.task?.task_id ?? row.id, "completed")
        .then(() => {
          message.success("Task completed.");
          reload();
        })
        .catch((err) => {
          message.error(
            err?.response?.data?.detail ||
              "Couldn't complete the task. Please try again."
          );
        })
        .finally(() => setBusyTaskKey(null));
    },
    [reload]
  );

  const acknowledgeFinished = useCallback(
    (row) => {
      setFlow((prev) =>
        prev
          ? {
              ...prev,
              finished_by_others: (prev.finished_by_others || []).filter(
                (other) => other.key !== row.key
              ),
            }
          : prev
      );
      acknowledgeFinishedWork(row.id).catch(() => {
        message.error("Couldn't acknowledge that. Please try again.");
        reload({ silent: true });
      });
    },
    [reload]
  );

  const allInMyCourt = useMemo(() => flow?.in_my_court || [], [flow]);
  // Work Items v2 (Matthew, 11/2 — Heather: "all the requests for me to
  // sign something in one place"): one chip narrows the court to items
  // whose ask is Approve or sign. Nothing else about the page changes.
  const signOffRows = useMemo(
    () => allInMyCourt.filter((row) => row.work_item?.ask === "Approve"),
    [allInMyCourt]
  );
  const [signOffsOnly, setSignOffsOnly] = useState(false);
  // The filter only bites while there is something to filter to: once the
  // last sign-off leaves the court the chip disappears, so the switch must
  // not stay stuck on an empty page (Bugbot, #942).
  const inMyCourt =
    signOffsOnly && signOffRows.length > 0 ? signOffRows : allInMyCourt;
  const following = useMemo(() => flow?.following || [], [flow]);
  const finished = flow?.finished_by_others || [];
  const clocked = useMemo(() => onAClockRows(inMyCourt), [inMyCourt]);
  const clientGroups = useMemo(() => groupByClient(inMyCourt), [inMyCourt]);
  const categoryGroups = useMemo(
    () => groupByCategory(inMyCourt),
    [inMyCourt]
  );
  const followingGroups = useMemo(() => groupByClient(following), [following]);

  const viewing = flow?.viewing;
  const readOnly = Boolean(person) && (viewing ? !viewing.is_self : true);
  const viewedName = viewing?.name || person?.name || "";

  const toggleExpanded = (key) =>
    setExpandedKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  const isExpandable = (row) => canExpandInPlace(row, readOnly);

  const renderRow = (row, { showKind, showMatter }) => (
    <WorkRow
      key={row.key}
      row={row}
      showKind={showKind}
      showMatter={showMatter}
      readOnly={readOnly}
      expanded={Boolean(expandedKeys[row.key])}
      onToggleExpand={() => toggleExpanded(row.key)}
      onOpen={() => openRow(row)}
      onCompleteTask={() => completeTask(row)}
      taskBusy={busyTaskKey === row.key}
      onActed={reload}
    />
  );

  /** Within a card/window, split by activation: active rows render as
   * always; outside (unless loud) and snoozed rows fold into their own
   * collapsed block, titled but never counted (Matthew, 9/4). Blocked
   * rows stay in `active`, greyed in place by WorkRow itself. */
  const renderGroupRows = (rows, { showKind, showMatter }) => {
    const { active, outside, snoozed } = partitionByActivation(rows);
    return (
      <>
        {active.map((row) => renderRow(row, { showKind, showMatter }))}
        <WorkFold
          kind="outside"
          title="Waiting on client / vendor"
          rows={outside}
        >
          {outside.map((row) => renderRow(row, { showKind, showMatter }))}
        </WorkFold>
        <WorkFold kind="snoozed" title="Snoozed" rows={snoozed}>
          {snoozed.map((row) => renderRow(row, { showKind, showMatter }))}
        </WorkFold>
      </>
    );
  };

  if (loadStatus === "failed") {
    return (
      <div data-testid="my-work-tab">
        <DegradedBanner role="alert">
          Couldn&apos;t load your work just now.{" "}
          <Button size="small" onClick={reload}>
            Try again
          </Button>
        </DegradedBanner>
      </div>
    );
  }

  if (loadStatus === "loading" && !flow) {
    return (
      <LoadingNote role="status">
        <Spin size="small" /> Gathering your work…
      </LoadingNote>
    );
  }

  return (
    <div data-testid="my-work-tab">
      <TopBar>
        {!readOnly && (
          <Button onClick={() => setPickerOpen(true)}>Create Work Item</Button>
        )}
        <PersonSearch
          onPick={(picked) => {
            setPersonGone(false);
            setPerson(picked);
          }}
        />
      </TopBar>

      {personGone && <QuietNote role="status">No such person.</QuietNote>}

      {readOnly && (
        <ViewingBanner role="status">
          <span>
            You&apos;re seeing {viewedName}&apos;s work — the same view they
            have. Read-only.
          </span>
          <ViewingBannerSpacer />
          <Button size="small" onClick={() => setPerson(null)}>
            Back to my work
          </Button>
        </ViewingBanner>
      )}

      {flow?.work_items_enabled && flow?.salesforce_ok === false && (
        <DegradedBanner role="alert">
          Salesforce didn&apos;t answer, so work items are missing from this
          refresh — reviews, signatures, and tasks below are current.
        </DegradedBanner>
      )}

      <BoardAppealsTile readOnly={readOnly} />

      <FinishedStrip
        rows={finished}
        onAcknowledge={acknowledgeFinished}
        readOnly={readOnly}
      />

      {clocked.length > 0 && (
        <>
          <SectionHeaderRow>
            <SectionTitle>Due Soon</SectionTitle>
          </SectionHeaderRow>
          <ClockTableWrap>
            <ClockTable>
              <thead>
                <tr>
                  <th>Due</th>
                  <th>What</th>
                  <th>Kind</th>
                  <th>Client · Matter</th>
                  <th>From</th>
                </tr>
              </thead>
              <tbody>
                {clocked.map((row) => {
                  const due = dueInfo(row.due_on);
                  const matter = splitMatterLabel(row.matter_name);
                  const expandable = isExpandable(row);
                  const clockKey = `clock:${row.key}`;
                  const expanded = Boolean(expandedKeys[clockKey]);
                  return (
                    <React.Fragment key={`clock-${row.key}`}>
                      <tr data-testid={`clock-row-${row.key}`}>
                        <ClockDueCell
                          $urgency={due.urgency}
                          data-urgency={due.urgency}
                          data-testid={`clock-due-${row.key}`}
                        >
                          {due.label} <small>({due.detail})</small>
                        </ClockDueCell>
                        <td>
                          <ClockTitleButton
                            type="button"
                            title={row.title || ""}
                            aria-expanded={expandable ? expanded : undefined}
                            onClick={
                              expandable
                                ? () => toggleExpanded(clockKey)
                                : () => openRow(row)
                            }
                          >
                            {row.title || "Untitled"}
                          </ClockTitleButton>
                        </td>
                        <td>
                          <CategoryTag row={row} />
                        </td>
                        <td title={matter.full}>{matter.short}</td>
                        <td>{row.counterparty}</td>
                      </tr>
                      {expandable && expanded && (
                        <tr data-testid={`clock-expand-${row.key}`}>
                          <ClockExpandCell colSpan={5}>
                            <ExpandedWork
                              row={row}
                              matterLabel={matter.short || undefined}
                              readOnly={readOnly}
                              onActed={reload}
                              onOpen={() => openRow(row)}
                            />
                          </ClockExpandCell>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </ClockTable>
          </ClockTableWrap>
        </>
      )}

      {/* No heading here on purpose (redline #2) — the toggle names
          the grouping, the cards speak for themselves. */}
      <SectionHeaderRow>
        {signOffRows.length > 0 && (
          <TogglePill
            type="button"
            $active={signOffsOnly}
            aria-pressed={signOffsOnly}
            onClick={() => setSignOffsOnly((prev) => !prev)}
            title="Only the items waiting on your signature or approval"
            data-testid="sign-offs-toggle"
          >
            {`Sign-offs (${signOffRows.length})`}
          </TogglePill>
        )}
        <SectionSpacer />
        <TogglePill
          type="button"
          $active={groupMode === "client"}
          aria-pressed={groupMode === "client"}
          onClick={() => pickGroupMode("client")}
        >
          By client
        </TogglePill>
        <TogglePill
          type="button"
          $active={groupMode === "kind"}
          aria-pressed={groupMode === "kind"}
          onClick={() => pickGroupMode("kind")}
        >
          By category
        </TogglePill>
      </SectionHeaderRow>

      {inMyCourt.length === 0 ? (
        <PlateEmpty>
          {signOffsOnly && signOffRows.length > 0
            ? "Nothing is waiting on your signature."
            : readOnly
            ? "Nothing here right now."
            : "You're all caught up. Nice."}
        </PlateEmpty>
      ) : groupMode === "client" ? (
        <PackedCards
          items={clientGroups.map((group) => {
            const matter = splitMatterLabel(group.matterName);
            return {
              key: group.key,
              weight: 1 + group.rows.length,
              node: (
                <MatterCard data-testid={`client-card-${group.key}`}>
                  <MatterCardHeader title={matter.full || undefined}>
                    {matter.short || "No matter"}
                  </MatterCardHeader>
                  {renderGroupRows(group.rows, {
                    showKind: true,
                    showMatter: false,
                  })}
                </MatterCard>
              ),
            };
          })}
        />
      ) : (
        <PackedCards
          items={categoryGroups.map((group) => {
            // Only CAVC and BVA Briefs keep a window of their own;
            // everything else is a card per client, titled by the
            // client, rows tagged by what they are (Matthew, 9/9).
            if (group.matterKey != null) {
              const matter = splitMatterLabel(group.matterName);
              return {
                key: group.key,
                weight: 1 + group.rows.length,
                node: (
                  <MatterCard
                    data-testid={`kind-card-client-${group.matterKey}`}
                  >
                    <MatterCardHeader title={matter.full || undefined}>
                      {matter.short || "No matter"}
                    </MatterCardHeader>
                    {renderGroupRows(group.rows, {
                      showKind: true,
                      showMatter: false,
                    })}
                  </MatterCard>
                ),
              };
            }
            return {
              key: group.key,
              weight: 1 + group.rows.length,
              node: (
                <MatterCard data-testid={`kind-card-${group.category}`}>
                  <MatterCardHeader>
                    {CATEGORY_META[group.category].label}
                  </MatterCardHeader>
                  {renderGroupRows(group.rows, {
                    showKind: false,
                    showMatter: true,
                  })}
                </MatterCard>
              ),
            };
          })}
        />
      )}

      {/* Work Items v2 (11/2): cc'd items — visible, never in your court.
          Collapsed by default so a cc is never mistaken for work. */}
      {following.length > 0 && (
        <WorkFold kind="following" title="Following" rows={following}>
          <PackedCards
            items={followingGroups.map((group) => {
              const matter = splitMatterLabel(group.matterName);
              return {
                key: `following-${group.key}`,
                weight: 1 + group.rows.length,
                node: (
                  <MatterCard data-testid={`following-card-${group.key}`}>
                    <MatterCardHeader title={matter.full || undefined}>
                      {matter.short || "No matter"}
                    </MatterCardHeader>
                    {group.rows.map((row) =>
                      renderRow(row, { showKind: true, showMatter: false })
                    )}
                  </MatterCard>
                ),
              };
            })}
          />
        </WorkFold>
      )}

      <MatterPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(matter) => {
          setPickerOpen(false);
          setComposeMatterId(matter.id);
        }}
      />
      <CreateWorkItemModal
        open={composeMatterId != null}
        matterId={composeMatterId ?? undefined}
        onClose={() => setComposeMatterId(null)}
        onCreated={reloadAfterCreate}
      />
    </div>
  );
};

MyWorkTab.propTypes = {
  /** Home's document-opening row-props factory (deleted-file check +
   * viewer deep link); called with `{ document }`. */
  getRowProps: PropTypes.func.isRequired,
  /** Variant that auto-opens the review-notes thread on arrival —
   * used only for doc_review rows. */
  getReviewRowProps: PropTypes.func,
};

export default MyWorkTab;
